import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The header slot.
 *
 * A tab screen's header must not scroll away with its content, so it cannot
 * simply be the first element inside `<main>` — `<main>` is the scroll
 * container. The slot lives above it in the column and a screen fills it by
 * rendering `<ShellHeader>`, which portals into the node `AppShell` published.
 *
 * The same shape as `OverlayContext`, for the same reason: the target is a node
 * inside the column, so it is passed through React rather than looked up
 * globally. A screen that renders no `ShellHeader` gets no header, and the slot
 * collapses to nothing — `empty:hidden` on the container. SUAP-0104.
 */
export const HeaderSlotContext = createContext<HTMLElement | null>(null)

interface ShellHeaderProps {
  children: ReactNode
}

export function ShellHeader({ children }: ShellHeaderProps) {
  const slot = useContext(HeaderSlotContext)
  if (slot === null) return null
  return createPortal(
    // min-w-0 so a long unbreakable title truncates inside the column instead of
    // pushing past its edge — a flex child's automatic minimum size is its
    // content, and a review measured a title reaching x=613 in a 360px column.
    <div className="flex min-h-14 min-w-0 items-center gap-2 border-b border-hairline bg-surface px-4 [&>*]:min-w-0 [&>*]:truncate">
      {children}
    </div>,
    slot,
  )
}
